import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import ServiceCarousel from './ServiceCarousel';
import { 
  CheckCircle, 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  FileText,
  Shield,
  Camera,
  Calculator,
  Car,
  Wrench,
  Star,
  Calendar,
  MessageSquare,
  HelpCircle,
  AlertTriangle
} from 'lucide-react';
import { mockData } from '../mock';

import TerminKalender from './TerminKalender';

const HomePage = React.memo(React.forwardRef((props, ref) => {
  const [isTerminKalenderOpen, setIsTerminKalenderOpen] = useState(false);
  const [isProcessVisible, setIsProcessVisible] = useState(false);
  const [isGutachtenVisible, setIsGutachtenVisible] = useState(false);

  const servicesRef = useRef(null);
  const processRef = useRef(null);
  const contactRef = useRef(null);
  const gutachtenRef = useRef(null);
  const faqRef = useRef(null);
  const testimonialsRef = useRef(null);

  const scrollToSection = (section) => {
    const refs = {
      services: servicesRef,
      process: processRef,
      contact: contactRef,
      faq: faqRef,
      testimonials: testimonialsRef
    };
    refs[section]?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  React.useImperativeHandle(ref, () => ({
    scrollToSection
  }));

  // Intersection Observer für Scroll-triggered Animationen
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.target === processRef.current) {
            setIsProcessVisible(true);
            observer.unobserve(entry.target);
          }
          if (entry.isIntersecting && entry.target === gutachtenRef.current) {
            setIsGutachtenVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.3, // Animation startet wenn 30% der Section sichtbar ist
        rootMargin: '-50px 0px' // Kleine Verzögerung für besseren Effekt
      }
    );

    if (processRef.current) {
      observer.observe(processRef.current);
    }
    if (gutachtenRef.current) {
      observer.observe(gutachtenRef.current);
    }

    return () => {
      if (processRef.current) {
        observer.unobserve(processRef.current);
      }
      if (gutachtenRef.current) {
        observer.unobserve(gutachtenRef.current);
      }
    };
  }, []);

  const serviceIcons = {
    'Schadengutachten': FileText,
    'Beweissicherung': Camera,
    'Kostenvoranschlag-Prüfung': Calculator,
    'Wertgutachten': Shield,
    'Restwertermittlung': Car,
    'Unfallrekonstruktion': Wrench
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[75vh] md:h-[87vh] flex items-start justify-start text-white">
        <div 
          className="absolute inset-0 bg-cover bg-right md:bg-top"
          style={{ 
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_rinno-kfz/artifacts/belrvqwh_moderne-kfz-mechaniker-komposition.jpg)` 
          }}
        />
        {/* Dunkles Overlay ohne blaue Farbe */}
        <div className="absolute inset-0 bg-black/60"></div>
        
        <div className="relative z-10 text-left w-full pl-4 md:pl-[170px] pr-8 pt-16 md:pt-24">
          <a 
            href="https://www.vfk-ev.de/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block mb-6 px-4 py-2 bg-[#0078B5] hover:bg-[#02B4D8] rounded-full text-lg font-medium text-white transition-colors cursor-pointer hero-text-animate"
          >
            {mockData.company.certification}
          </a>
          <h1 className="text-3xl md:text-7xl font-bold mb-4 md:mb-6 leading-tight hero-text-animate" style={{ animationDelay: '0.2s' }}>
            Ihr Starker Partner<br />im Schadenfall
          </h1>
          
          <div className="mb-6 md:mb-8 hero-text-animate" style={{ animationDelay: '0.4s' }}>
            <p className="text-sm md:text-lg text-white/90 mb-4 md:mb-6 max-w-3xl leading-relaxed">
              Wir erstellen unabhängige, fachgerechte Gutachten – klar, nachvollziehbar und rechtssicher. Damit Sie im Schadensfall oder bei Bewertungen auf der sicheren Seite sind.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 max-w-2xl">
              <div className="text-center md:text-left flex items-center">
                <span className="w-2 h-2 bg-[#004A6B] rounded-full mr-3 flex-shrink-0"></span>
                <h3 className="font-semibold text-sm md:text-base text-white">Präzise & objektiv</h3>
              </div>
              <div className="text-center md:text-left flex items-center">
                <span className="w-2 h-2 bg-[#004A6B] rounded-full mr-3 flex-shrink-0"></span>
                <h3 className="font-semibold text-sm md:text-base text-white">Anerkannt & rechtssicher</h3>
              </div>
              <div className="text-center md:text-left flex items-center">
                <span className="w-2 h-2 bg-[#004A6B] rounded-full mr-3 flex-shrink-0"></span>
                <h3 className="font-semibold text-sm md:text-base text-white">Transparent & nachvollziehbar</h3>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-start hero-text-animate" style={{ animationDelay: '0.6s' }}>
            <Button
              onClick={() => setIsTerminKalenderOpen(true)}
              size="lg"
              className="bg-[#0078B5] hover:bg-[#02B4D8] text-white font-semibold text-lg px-8 py-4 h-auto transition-colors"
            >
              Termin vereinbaren
            </Button>
          </div>
        </div>
      </section>

      {/* Warum ein Gutachten Section */}
      <section ref={gutachtenRef} className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-bold text-gray-900 mb-4" style={{ fontSize: window.innerWidth >= 768 ? '36px' : '26px' }}>
              <span className="block md:hidden">Warum ein Gutachten vom Sachverständigenbüro Rinno?</span>
              <span className="hidden md:block">Warum ein Gutachten vom KFZ-Sachverständigenbüro Rinno?</span>
            </h2>
            <p className="text-gray-600 max-w-4xl mx-auto" style={{ fontSize: window.innerWidth >= 768 ? '20px' : '16px' }}>
              Unsere unabhängigen Gutachten schaffen Sicherheit, Transparenz und Vertrauen – für Sie und Ihr Fahrzeug.
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
            {/* Vorteile Box - Desktop: von links, Mobile: von unten */}
            <Card 
              className={`bg-white border-2 border-green-500 rounded-xl overflow-hidden transform transition-all duration-1000 ${
                isGutachtenVisible 
                  ? 'translate-x-0 translate-y-0 opacity-100' 
                  : 'md:-translate-x-20 translate-y-10 md:translate-y-0 opacity-0'
              }`}
              style={{
                boxShadow: '0 25px 50px -12px rgba(34, 197, 94, 0.25), 0 10px 20px -5px rgba(34, 197, 94, 0.1)',
                transitionDelay: isGutachtenVisible ? '200ms' : '0ms'
              }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_rinno-autoservice/artifacts/6w0nj4ud_mittlerer-schussmann-der-auto-uberpruft.jpg"
                alt="Sachverständiger bei der Fahrzeugprüfung"
                className="w-full h-48 md:h-64 object-cover"
              />
              <div className="p-6">
                <h3 className="text-2xl font-bold text-green-600 mb-6 text-center">Mit Gutachten</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-green-500 rounded-full flex-shrink-0">
                      <Calculator size={16} className="text-white" />
                    </div>
                    <p className="text-gray-700">Klare und faire Bewertung des tatsächlichen Schadens</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-green-500 rounded-full flex-shrink-0">
                      <Clock size={16} className="text-white" />
                    </div>
                    <p className="text-gray-700">Schnelle Abwicklung durch die Versicherung</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-green-500 rounded-full flex-shrink-0">
                      <Shield size={16} className="text-white" />
                    </div>
                    <p className="text-gray-700">Unabhängige Beratung nur für Ihre Interessen</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-green-500 rounded-full flex-shrink-0">
                      <FileText size={16} className="text-white" />
                    </div>
                    <p className="text-gray-700">Gerichtsfeste Dokumentation</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-green-500 rounded-full flex-shrink-0">
                      <Star size={16} className="text-white" />
                    </div>
                    <p className="text-gray-700">Zusammenarbeit & Weitergabe an Rechtsanwälte</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Nachteile Box - Desktop: von rechts, Mobile: von unten */}
            <Card 
              className={`bg-white border-2 border-red-500 rounded-xl overflow-hidden transform transition-all duration-1000 ${
                isGutachtenVisible 
                  ? 'translate-x-0 translate-y-0 opacity-100' 
                  : 'md:translate-x-20 translate-y-10 md:translate-y-0 opacity-0'
              }`}
              style={{
                boxShadow: '0 25px 50px -12px rgba(239, 68, 68, 0.25), 0 10px 20px -5px rgba(239, 68, 68, 0.1)',
                transitionDelay: isGutachtenVisible ? '400ms' : '0ms'
              }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_rinno-autoservice/artifacts/afyjipuf_autotransporter-abschleppwagen-wahrend-des-arbeitens-mit-einem-gesperrten-gurttransport-anderen-grunen-autos.jpg"
                alt="Abschleppwagen bei der Arbeit"
                className="w-full h-48 md:h-64 object-cover"
              />
              <div className="p-6">
                <h3 className="text-2xl font-bold text-red-600 mb-6 text-center">Ohne Gutachten</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-red-500 rounded-full flex-shrink-0">
                      <HelpCircle size={16} className="text-white" />
                    </div>
                    <p className="text-gray-700">Unklare Schadensermittlung durch Versicherung</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-red-500 rounded-full flex-shrink-0">
                      <Clock size={16} className="text-white" />
                    </div>
                    <p className="text-gray-700">Längere Wartezeiten bei der Regulierung</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-red-500 rounded-full flex-shrink-0">
                      <AlertTriangle size={16} className="text-white" />
                    </div>
                    <p className="text-gray-700">Einseitige Interessenvertretung der Versicherung</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-red-500 rounded-full flex-shrink-0">
                      <FileText size={16} className="text-white" />
                    </div>
                    <p className="text-gray-700">Fehlende rechtliche Absicherung</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-red-500 rounded-full flex-shrink-0">
                      <Calculator size={16} className="text-white" />
                    </div>
                    <p className="text-gray-700">Risiko einer zu niedrigen Schadensbewertung</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Certificates Section - REMOVED */}

      {/* Process Section - EXTREME BRUTALE ANIMATIONEN! */}
      <section ref={processRef} className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        {/* Hintergrund-Effekte entfernt für sauberes Design */}
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Unser Prozess
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
              Vom ersten Kontakt bis zur erfolgreichen Schadensregulierung
            </p>
          </div>
          
          {/* Desktop: Scroll-triggered Animationen Grid Layout */}
          <div className="hidden md:grid md:grid-cols-4 gap-6 lg:gap-8">
            {mockData.process.map((step, index) => (
              <div 
                key={index}
                className={`group relative transition-opacity duration-300 ${
                  isProcessVisible ? 'process-box-animate' : 'opacity-0'
                }`}
                style={{
                  animation: isProcessVisible ? `backToFront 1.2s ease-out forwards` : 'none',
                  opacity: isProcessVisible ? 1 : 0
                }}
              >
                {/* Subtiler Glow Ring */}
                <div className="absolute -inset-1 bg-gradient-to-r from-[#0078B5] via-[#02B4D8] to-[#0078B5] rounded-2xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
                
                <div className="relative bg-white rounded-2xl shadow-xl border border-gray-100 p-6 cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:border-[#0078B5]">
                  {/* Step Number - statisch nach Animation */}
                  <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-[#0078B5] to-[#02B4D8] text-white text-lg font-bold rounded-full mb-4 mx-auto relative transform transition-all duration-300 group-hover:scale-110">
                    {step.step}
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-lg font-bold text-gray-900 mb-3 text-center group-hover:text-[#0078B5] transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm text-center group-hover:text-gray-800 transition-colors duration-300">
                    {step.description}
                  </p>
                  
                  {/* Subtiler Hover Glow */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#0078B5]/5 to-[#02B4D8]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Mobile: Scroll-triggered Animationen mit geraden Strichen */}
          <div className="md:hidden relative">
            {mockData.process.map((step, index) => (
              <div key={index} className="relative mb-8 last:mb-0">
                {/* GERADE STRICH-Verbindung (nur nicht beim letzten Element) */}
                {index < mockData.process.length - 1 && (
                  <div 
                    className={`absolute -bottom-4 left-1/2 transform -translate-x-1/2 z-10 transition-opacity duration-300 ${
                      isProcessVisible ? 'opacity-100' : 'opacity-0'
                    }`}
                    style={{
                      transitionDelay: isProcessVisible ? '1s' : '0ms'
                    }}
                  >
                    <div className="w-1 h-8 bg-gradient-to-b from-[#0078B5] to-[#02B4D8] rounded-full"></div>
                  </div>
                )}
                
                {/* Content Card mit scroll-triggered Mobile-Effekten */}
                <div 
                  className={`bg-white rounded-2xl shadow-xl border border-gray-100 p-6 mx-4 cursor-pointer relative transform transition-all duration-300 hover:scale-105 hover:shadow-2xl ${
                    isProcessVisible ? 'mobile-process-box-animate' : 'opacity-0'
                  }`}
                  style={{
                    animation: isProcessVisible ? `backToFrontMobile 1.2s ease-out forwards` : 'none',
                    opacity: isProcessVisible ? 1 : 0
                  }}
                >
                  {/* Step Number - statisch nach Animation */}
                  <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-[#0078B5] to-[#02B4D8] text-white text-sm font-bold rounded-full mb-4 mx-auto relative transform transition-all duration-300 hover:scale-110">
                    {step.step}
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-xl font-bold text-gray-900 mb-3 text-center transition-colors duration-300 hover:text-[#0078B5]">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm text-center transition-colors duration-300 hover:text-gray-800">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Scroll-triggered CSS Animationen - Optimiert */}
          <style jsx>{`
            /* Desktop: Alle Boxen gleichzeitig von hinten nach vorne */}
            @keyframes backToFront {
              0% { 
                opacity: 0; 
                transform: perspective(1000px) translateZ(-100px) scale(0.8);
              }
              100% { 
                opacity: 1; 
                transform: perspective(1000px) translateZ(0px) scale(1);
              }
            }
            
            /* Mobile: Alle Boxen gleichzeitig von hinten nach vorne */
            @keyframes backToFrontMobile {
              0% { 
                opacity: 0; 
                transform: scale(0.7) translateY(30px);
              }
              100% { 
                opacity: 1; 
                transform: scale(1) translateY(0);
              }
            }
            
            /* Initial unsichtbare Boxen */
            .process-box-animate {
              opacity: 0;
            }
            
            .mobile-process-box-animate {
              opacity: 0;
            }
          `}</style>
        </div>
      </section>

      {/* Services Section - NOW POSITIONED AFTER PROCESS */}
      <section ref={servicesRef} className="relative" style={{ paddingTop: '40px', paddingBottom: '40px' }}>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_rinno-autoservice/artifacts/pmf7dm8y_geschaftsmann-der-einem-burodesktop-arbeitet.jpg)` 
          }}
        />
        {/* Dunkles Overlay wie bei Hero Section */}
        <div className="absolute inset-0 bg-black/60"></div>
        
        <div className="relative z-10 container mx-auto px-4">
          <div className="text-center mb-4 md:mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2 md:mb-4 whitespace-nowrap">Service & Leistungen</h2>
            <p className="text-sm sm:text-xl text-white/90 max-w-3xl mx-auto">
              Professionelle KFZ-Sachverständigenleistungen für Privatkunden, Unternehmen, Versicherungen, Anwälte und Gerichte
            </p>
          </div>
          
          <ServiceCarousel />
        </div>
      </section>

      {/* FAQ Section */}
      <section ref={faqRef} className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">FAQ – Häufige Fragen</h2>
            <p className="text-xl text-gray-600">
              Die wichtigsten Antworten auf häufig gestellte Fragen
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="border-none">
                <AccordionTrigger className="text-left px-6 py-4 text-black font-bold text-lg hover:no-underline rounded-lg" style={{ backgroundColor: '#E0F7FA' }}>
                  Wer bezahlt den Sachverständigen nach einem Unfall?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 mt-2 text-black rounded-lg border-2 border-solid" style={{ backgroundColor: '#FFFFFF', borderColor: '#333333' }}>
                  In der Regel übernimmt die gegnerische Versicherung die Kosten des Gutachtens, wenn Sie unverschuldet in einen Unfall geraten sind.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="border-none">
                <AccordionTrigger className="text-left px-6 py-4 text-black font-bold text-lg hover:no-underline rounded-lg" style={{ backgroundColor: '#E0F7FA' }}>
                  Brauche ich wirklich ein Gutachten, wenn die Werkstatt schon einen Kostenvoranschlag erstellt?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 mt-2 text-black rounded-lg border-2 border-solid" style={{ backgroundColor: '#FFFFFF', borderColor: '#333333' }}>
                  Ein Kostenvoranschlag reicht oft nicht aus, da er nur die reinen Reparaturkosten auflistet.
                  Ein Gutachten enthält zusätzlich Informationen wie Wertminderung, Restwert, Nutzungsausfall und Beweissicherung, die für die Schadenregulierung wichtig sind.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="border-none">
                <AccordionTrigger className="text-left px-6 py-4 text-black font-bold text-lg hover:no-underline rounded-lg" style={{ backgroundColor: '#E0F7FA' }}>
                  Wie schnell kann ein Termin für ein Gutachten vereinbart werden?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 mt-2 text-black rounded-lg border-2 border-solid" style={{ backgroundColor: '#FFFFFF', borderColor: '#333333' }}>
                  In der Regel erhalten Sie innerhalb von 24 Stunden einen Termin.
                  Bei größeren Schäden oder Notfällen auch kurzfristig.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="border-none">
                <AccordionTrigger className="text-left px-6 py-4 text-black font-bold text-lg hover:no-underline rounded-lg" style={{ backgroundColor: '#E0F7FA' }}>
                  Kann das Gutachten auch bei mir zu Hause oder an der Werkstatt erstellt werden?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 mt-2 text-black rounded-lg border-2 border-solid" style={{ backgroundColor: '#FFFFFF', borderColor: '#333333' }}>
                  Ja, wir kommen auf Wunsch direkt zu Ihnen oder in die Werkstatt.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="border-none">
                <AccordionTrigger className="text-left px-6 py-4 text-black font-bold text-lg hover:no-underline rounded-lg" style={{ backgroundColor: '#E0F7FA' }}>
                  Wie lange dauert die Erstellung eines Gutachtens?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 mt-2 text-black rounded-lg border-2 border-solid" style={{ backgroundColor: '#FFFFFF', borderColor: '#333333' }}>
                  Je nach Schadenumfang beträgt die Bearbeitungszeit zwischen 1–3 Werktagen.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="border-none">
                <AccordionTrigger className="text-left px-6 py-4 text-black font-bold text-lg hover:no-underline rounded-lg" style={{ backgroundColor: '#E0F7FA' }}>
                  Wer bekommt das Gutachten?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 mt-2 text-black rounded-lg border-2 border-solid" style={{ backgroundColor: '#FFFFFF', borderColor: '#333333' }}>
                  Sie erhalten das Gutachten persönlich.
                  Auf Wunsch senden wir es direkt an Versicherungen, Anwälte oder Werkstätten.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7" className="border-none">
                <AccordionTrigger className="text-left px-6 py-4 text-black font-bold text-lg hover:no-underline rounded-lg" style={{ backgroundColor: '#E0F7FA' }}>
                  Kann ich auch ohne Unfall ein Gutachten beauftragen?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 mt-2 text-black rounded-lg border-2 border-solid" style={{ backgroundColor: '#FFFFFF', borderColor: '#333333' }}>
                  Ja, z. B. für ein Wertgutachten beim Fahrzeugverkauf, Leasingrückgabe oder für Oldtimer.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-8" className="border-none">
                <AccordionTrigger className="text-left px-6 py-4 text-black font-bold text-lg hover:no-underline rounded-lg" style={{ backgroundColor: '#E0F7FA' }}>
                  Was passiert, wenn ich am Unfall schuld bin?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 mt-2 text-black rounded-lg border-2 border-solid" style={{ backgroundColor: '#FFFFFF', borderColor: '#333333' }}>
                  In diesem Fall übernimmt in der Regel Ihre eigene Versicherung die Kosten.
                  Ein unabhängiges Gutachten kann trotzdem sinnvoll sein, um den Schaden korrekt zu erfassen.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-9" className="border-none">
                <AccordionTrigger className="text-left px-6 py-4 text-black font-bold text-lg hover:no-underline rounded-lg" style={{ backgroundColor: '#E0F7FA' }}>
                  Wird ein Kurzgutachten oder Kostenvoranschlag auch akzeptiert?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 mt-2 text-black rounded-lg border-2 border-solid" style={{ backgroundColor: '#FFFFFF', borderColor: '#333333' }}>
                  Ja, bei kleineren Schäden reicht oft ein Kurzgutachten oder ein Kostenvoranschlag.
                  Bei größeren Schäden empfehlen wir ein vollständiges Schadengutachten.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-10" className="border-none">
                <AccordionTrigger className="text-left px-6 py-4 text-black font-bold text-lg hover:no-underline rounded-lg" style={{ backgroundColor: '#E0F7FA' }}>
                  Wie erreiche ich Sie im Notfall?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 mt-2 text-black rounded-lg border-2 border-solid" style={{ backgroundColor: '#FFFFFF', borderColor: '#333333' }}>
                  Sie können uns telefonisch rund um die Uhr erreichen.
                  Wir kümmern uns sofort um Ihr Anliegen.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section ref={testimonialsRef} className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Kundenstimmen</h2>
            <p className="text-xl text-gray-600">
              Was unsere Kunden über unsere Arbeit sagen
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockData.testimonials.map((testimonial, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="flex items-center space-x-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <CardTitle className="text-lg">{testimonial.name}</CardTitle>
                  <CardDescription className="text-sm">
                    {testimonial.location} • {testimonial.service}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 italic">"{testimonial.text}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section ref={contactRef} className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Kontakt & Standort</h2>
          </div>
          
          <div className="max-w-6xl mx-auto space-y-12">
            {/* Kontaktinformationen Box */}
            <Card className="bg-[#004A6B] border-2 border-[#0078B5] rounded-xl p-4 md:p-8" style={{
              boxShadow: '0 25px 50px -12px rgba(0, 120, 181, 0.25), 0 10px 20px -5px rgba(0, 120, 181, 0.1)'
            }}>
              <h3 className="text-xl md:text-2xl font-bold text-white text-center mb-6 md:mb-8">Kontaktinformationen</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
                {/* Mobile: All items stacked, Desktop: Two columns */}
                
                {/* Telefon */}
                <div className="flex items-start space-x-3">
                  <div className="p-2 md:p-3 bg-[#0078B5] rounded-full flex-shrink-0">
                    <Phone size={16} className="md:hidden text-white" />
                    <Phone size={24} className="hidden md:block text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg md:text-lg font-semibold text-white" style={{ marginBottom: window.innerWidth >= 768 ? '2.5px' : '1px' }}>Telefon</h4>
                    <a 
                      href={`tel:${mockData.company.address.phone}`} 
                      className="text-lg md:text-xl font-medium text-[#93E2F1] hover:text-white transition-colors"
                    >
                      {mockData.company.address.phone}
                    </a>
                  </div>
                </div>
                
                {/* E-Mail */}
                <div className="flex items-start space-x-3">
                  <div className="p-2 md:p-3 bg-[#0078B5] rounded-full flex-shrink-0">
                    <Mail size={16} className="md:hidden text-white" />
                    <Mail size={24} className="hidden md:block text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg md:text-lg font-semibold text-white" style={{ marginBottom: window.innerWidth >= 768 ? '2.5px' : '1px' }}>E-Mail</h4>
                    <a 
                      href={`mailto:${mockData.company.address.email}`} 
                      className="text-lg md:text-xl font-medium text-[#93E2F1] hover:text-white transition-colors break-words"
                    >
                      {mockData.company.address.email}
                    </a>
                  </div>
                </div>
                
                {/* Adresse */}
                <div className="flex items-start space-x-3">
                  <div className="p-2 md:p-3 bg-[#0078B5] rounded-full flex-shrink-0">
                    <MapPin size={16} className="md:hidden text-white" />
                    <MapPin size={24} className="hidden md:block text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg md:text-lg font-semibold text-white" style={{ marginBottom: window.innerWidth >= 768 ? '2.5px' : '1px' }}>Adresse</h4>
                    <p className="text-lg md:text-xl font-medium text-white">
                      {mockData.company.address.street}, {mockData.company.address.city}
                    </p>
                  </div>
                </div>
                
                {/* Öffnungszeiten */}
                <div className="flex items-start space-x-3">
                  <div className="p-2 md:p-3 bg-[#0078B5] rounded-full flex-shrink-0">
                    <Clock size={16} className="md:hidden text-white" />
                    <Clock size={24} className="hidden md:block text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg md:text-lg font-semibold text-white" style={{ marginBottom: window.innerWidth >= 768 ? '2.5px' : '1px' }}>Öffnungszeiten</h4>
                    <div className="text-lg md:text-xl font-medium text-white">
                      {mockData.company.address.hours.split('\n').map((line, index) => (
                        <div key={index}>{line}</div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
            
            {/* Unser Standort */}
            <Card className="bg-[#004A6B] border-2 border-[#0078B5] rounded-xl p-4 md:p-8 space-y-6" style={{
              boxShadow: '0 25px 50px -12px rgba(0, 120, 181, 0.25), 0 10px 20px -5px rgba(0, 120, 181, 0.1)'
            }}>
              <h3 className="text-xl md:text-3xl font-bold text-white text-center">Unser Standort</h3>
              
              {/* Google Maps */}
              <div className="w-full">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2594.8!2d11.0879!3d49.4521!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x479f57a9e41f5555%3A0x1234567890abcdef!2sHolzschuherstra%C3%9Fe%2042%2C%2090439%20N%C3%BCrnberg%2C%20Germany!5e0!3m2!1sen!2sde!4v1699000000000!5m2!1sen!2sde"
                  width="100%"
                  height="350"
                  className="rounded-xl"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="KFZ-Sachverständigenbüro Rinno Standort"
                ></iframe>
              </div>
              
              {/* Address Line & Info Text */}
              <div className="text-center space-y-3">
                <p className="text-lg md:text-xl font-semibold text-white">
                  <a 
                    href="https://maps.google.com?q=Holzschuherstraße 42, 90439 Nürnberg"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-[#93E2F1] transition-colors"
                  >
                    Holzschuherstraße 42, 90439 Nürnberg
                  </a>
                </p>
                <p className="text-xs md:text-lg text-white">
                  Besuchen Sie uns vor Ort oder vereinbaren Sie einen Termin für eine Begutachtung bei Ihnen
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Termin Kalender Dialog */}
      <TerminKalender 
        isOpen={isTerminKalenderOpen} 
        onClose={() => setIsTerminKalenderOpen(false)} 
      />
    </div>
  );
}));

export default HomePage;